<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  // On views where the status is a fixed queue invariant (e.g. AI handling
  // forces `pending`), the status control is hidden and only sort remains.
  hideStatusFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();

const { uiSettings, updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const showActionsDropdown = ref(false);
const triggerRef = ref(null);
const dropdownRef = ref(null);

// The conversation list column clips its overflow, so the panel is teleported
// out and pinned to the trigger instead of being absolutely placed inside it.
const { fixedPosition, updatePosition } = useDropdownPosition(
  triggerRef,
  dropdownRef,
  showActionsDropdown,
  { align: 'end' }
);

const toggleDropdown = async () => {
  showActionsDropdown.value = !showActionsDropdown.value;
  if (!showActionsDropdown.value) return;
  await nextTick();
  updatePosition();
};

const handleClickOutside = event => {
  if (triggerRef.value?.contains(event.target)) return;
  showActionsDropdown.value = false;
};

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN;
});

const currentSortBy = computed(() => {
  return (
    chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const chatStatusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

// S1 sort model: a field list plus a direction toggle instead of the flat
// ten-row menu. Each field names its two directions; `unread` has only one.
const sortFields = computed(() => [
  {
    key: 'last_activity',
    label: t('CHAT_LIST.CHAT_SORT.FIELDS.LAST_ACTIVITY'),
    primary: {
      value: 'last_activity_at_desc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.NEWEST'),
    },
    secondary: {
      value: 'last_activity_at_asc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.OLDEST'),
    },
  },
  {
    key: 'created',
    label: t('CHAT_LIST.CHAT_SORT.FIELDS.CREATED'),
    primary: {
      value: 'created_at_desc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.NEWEST'),
    },
    secondary: {
      value: 'created_at_asc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.OLDEST'),
    },
  },
  {
    key: 'priority',
    label: t('CHAT_LIST.CHAT_SORT.FIELDS.PRIORITY'),
    primary: {
      value: 'priority_desc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.HIGHEST'),
    },
    secondary: {
      value: 'priority_asc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.LOWEST'),
    },
  },
  {
    key: 'unread',
    label: t('CHAT_LIST.CHAT_SORT.FIELDS.UNREAD'),
    primary: {
      value: 'unread',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.HIGHEST'),
    },
    secondary: null,
  },
  {
    key: 'waiting',
    label: t('CHAT_LIST.CHAT_SORT.FIELDS.WAITING'),
    primary: {
      value: 'waiting_since_asc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.LONGEST'),
    },
    secondary: {
      value: 'waiting_since_desc',
      label: t('CHAT_LIST.CHAT_SORT.DIRECTIONS.SHORTEST'),
    },
  },
]);

const activeSortField = computed(
  () =>
    sortFields.value.find(f =>
      [f.primary.value, f.secondary?.value].includes(currentSortBy.value)
    ) || sortFields.value[0]
);

const activeSortDirectionLabel = computed(() => {
  const field = activeSortField.value;
  if (field.secondary?.value === currentSortBy.value)
    return field.secondary.label;
  return field.primary.label;
});

const activeChatStatusLabel = computed(
  () =>
    chatStatusOptions.value.find(m => m.value === currentStatusFilter.value)
      ?.label || ''
);

const saveSelectedFilter = (type, value) => {
  // When the status control is hidden, the list runs on a forced status that
  // must not leak into the stored preference on a sort-only save.
  let status = type === 'status' ? value : currentStatusFilter.value;
  if (props.hideStatusFilter) {
    status = uiSettings.value.conversations_filter_by?.status;
  }
  updateUISettings({
    conversations_filter_by: {
      status,
      order_by: type === 'sort' ? value : currentSortBy.value,
    },
  });
};

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};

// Picking a field keeps the current direction slot when the field has it;
// picking a direction re-applies the active field with that direction.
const selectSortField = field => {
  const onSecondary =
    activeSortField.value.secondary?.value === currentSortBy.value;
  handleSortChange(
    onSecondary && field.secondary ? field.secondary.value : field.primary.value
  );
};
</script>

<template>
  <div ref="triggerRef" class="relative flex">
    <button
      v-tooltip.right="$t('INBOX.LIST.DISPLAY_DROPDOWN')"
      type="button"
      data-testid="display-menu-trigger"
      :aria-label="$t('INBOX.LIST.DISPLAY_DROPDOWN')"
      :aria-expanded="showActionsDropdown"
      class="grid rounded-full size-7 shrink-0 place-items-center bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="toggleDropdown()"
    >
      <span class="i-lucide-settings-2 size-4" />
    </button>
    <TeleportWithDirection>
      <div
        v-if="showActionsDropdown"
        ref="dropdownRef"
        v-on-click-outside="handleClickOutside"
        class="bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-2xl p-4 shadow-lg shadow-n-slate-12/10"
        :class="fixedPosition.class"
        :style="fixedPosition.style"
      >
        <div
          v-if="!hideStatusFilter"
          class="flex items-center justify-between gap-2"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
          </span>
          <SelectMenu
            :model-value="chatStatusFilter"
            :options="chatStatusOptions"
            :label="activeChatStatusLabel"
            sub-menu-position="bottom"
            @update:model-value="handleStatusChange"
          />
        </div>
        <div :class="{ 'mt-4': !hideStatusFilter }">
          <span
            class="block mb-1.5 text-[10px] font-semibold tracking-wider uppercase text-n-slate-10"
          >
            {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
          </span>
          <div class="flex flex-col gap-0.5">
            <button
              v-for="field in sortFields"
              :key="field.key"
              type="button"
              role="menuitemradio"
              :aria-checked="field.key === activeSortField.key"
              class="flex items-center w-full gap-2 rounded-lg px-2 py-1.5 text-sm"
              :class="
                field.key === activeSortField.key
                  ? 'bg-n-violet-3 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="selectSortField(field)"
            >
              <span class="flex-1 text-left rtl:text-right">
                {{ field.label }}
              </span>
              <span
                v-if="field.key === activeSortField.key"
                class="text-xs text-n-violet-11"
              >
                {{ activeSortDirectionLabel }}
              </span>
            </button>
          </div>
          <div class="flex gap-1.5 pt-2 mt-2 border-t border-n-weak">
            <button
              type="button"
              class="flex-1 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="
                currentSortBy === activeSortField.primary.value
                  ? 'bg-n-violet-3 text-n-violet-11'
                  : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="handleSortChange(activeSortField.primary.value)"
            >
              {{ activeSortField.primary.label }}
            </button>
            <button
              v-if="activeSortField.secondary"
              type="button"
              class="flex-1 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="
                currentSortBy === activeSortField.secondary.value
                  ? 'bg-n-violet-3 text-n-violet-11'
                  : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="handleSortChange(activeSortField.secondary.value)"
            >
              {{ activeSortField.secondary.label }}
            </button>
          </div>
        </div>
      </div>
    </TeleportWithDirection>
  </div>
</template>
